import { ArrowRight, BookOpen, Copy, Eye } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { HowItWorksModal } from './how-it-works-modal';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

interface HeroBannerProps {
  userEmail?: string;
  vanityUrl?: string;
  onPreviewIntakeForm: () => void;
}

export function HeroBanner({ userEmail, vanityUrl, onPreviewIntakeForm }: HeroBannerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const slug =
    vanityUrl ||
    userEmail
      ?.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-');
  const intakeUrl = slug ? `${window.location.origin}/${slug}` : '';

  const handleCopyLink = async () => {
    if (!intakeUrl) return;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <>
      <Card
        className="bg-[#F5EDD8] border-[#EAE0CB] shadow-sm"
        data-tour="doctor-dashboard-hero-banner"
      >
        <CardContent className="p-6">
          <div className="space-y-5">
            {/* Top Section: How It Works */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#A66B32] rounded-lg flex-shrink-0">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">How ExpertMD Works</h2>
                  <p className="text-sm text-gray-700 max-w-2xl">
                    Manage your expert witness practice from{' '}
                    <span className="font-medium text-[#3A2718]">Pre-Screenings</span> →{' '}
                    <span className="font-medium text-[#3A2718]">Cases</span> →{' '}
                    <span className="font-medium text-[#3A2718]">Records</span> →{' '}
                    <span className="font-medium text-[#3A2718]">Invoices</span> →{' '}
                    <span className="font-medium text-[#3A2718]">Payouts</span>.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setIsModalOpen(true)}
                variant="outline"
                className="border-[#3A2718]/20 text-[#3A2718] hover:bg-[#EAE0CB]/70 shadow-sm whitespace-nowrap"
              >
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Divider */}
            <div className="border-t border-[#3A2718]/15"></div>

            {/* Bottom Section: Share Intake Form */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                📋 Share Your Intake Form
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Distribute this link to attorneys to receive pre-screening requests
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="flex-1 bg-white border border-[#3A2718]/15 rounded-lg px-3 py-2 text-sm text-gray-700 font-mono overflow-x-auto">
                  {intakeUrl}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopyLink}
                    className="bg-[#3A2718] hover:bg-[#241610] text-white px-4 py-2 flex items-center gap-2 flex-1 sm:flex-initial"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                  <Button
                    onClick={onPreviewIntakeForm}
                    variant="outline"
                    className="border-[#3A2718]/20 text-[#3A2718] hover:bg-[#EAE0CB]/70 px-4 py-2 flex items-center gap-2 flex-1 sm:flex-initial"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <HowItWorksModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
